import type { CrmPreviewHandle } from '@/lib/crm/preview';

/**
 * Any object carrying an `exists_in_crm` flag (e.g. `EnrichedActionData` or
 * its `target_object`). Callers pass the object itself rather than
 * pre-extracting the field so every call site reads `exists_in_crm` through
 * this one helper.
 */
export interface CrmExistenceSource {
  exists_in_crm?: boolean;
}

/**
 * An entity id is only safe to pass to `MaybeEntityBadge`/`ActionEntityPreview`
 * (and, downstream, `EntityBadgeById`/`useCrmObjectSnapshot`, which issue a
 * real store/fetch lookup) when it either already exists in the CRM, or is
 * covered by a CRM preview overlay (a proposed create/edit projected by
 * `CrmPreviewProvider`). A synthetic create-only id that satisfies neither
 * must fall back to `undefined` — otherwise the downstream hook issues a
 * lookup for an id that will never resolve. `preview` is `undefined`/`null`
 * outside a `CrmPreviewProvider` (flag off), so this collapses to the plain
 * `existsInCrm` gate there.
 */
export function resolvableEntityId(
  entityId: string | undefined,
  crmExistenceSource: CrmExistenceSource | null | undefined,
  preview: CrmPreviewHandle | null | undefined
): string | undefined {
  if (!entityId) {
    return undefined;
  }
  return crmExistenceSource?.exists_in_crm || preview?.hasPreview(entityId)
    ? entityId
    : undefined;
}
